// Format (formato) metrics: parse a size string into a normalized quantity +
// unit family, so we can compute price-per-100-units, group formats by
// tipologia and pick the best value format. All display-only, admin-driven.
import { priceForSize } from "./variant";

// Trailing unit token → { family, factor to base unit, group key, per-100 label }.
const UNIT_DEFS = [
  { keys: ["kg", "chilogrammi", "chilogrammo", "chilo"], family: "weight", factor: 1000, group: "weight", per100: "100 g" },
  { keys: ["mg"], family: "weight", factor: 0.001, group: "weight", per100: "100 g" },
  { keys: ["g", "gr", "grammi", "grammo"], family: "weight", factor: 1, group: "weight", per100: "100 g" },
  { keys: ["ml", "millilitri"], family: "volume", factor: 1, group: "volume", per100: "100 ml" },
  { keys: ["cl"], family: "volume", factor: 10, group: "volume", per100: "100 ml" },
  { keys: ["l", "lt", "litri", "litro"], family: "volume", factor: 1000, group: "volume", per100: "100 ml" },
  { keys: ["cpr", "compresse", "compressa", "tab", "tabs", "tablet", "tavolette", "tavoletta"], family: "count", factor: 1, group: "compresse", per100: "100 cpr" },
  { keys: ["cps", "caps", "capsule", "capsula"], family: "count", factor: 1, group: "capsule", per100: "100 caps" },
  { keys: ["softgel", "softgels", "perle", "perla"], family: "count", factor: 1, group: "perle", per100: "100 perle" },
  { keys: ["stick", "bustine", "bustina", "sachet"], family: "count", factor: 1, group: "stick", per100: "100 stick" },
];

const detectUnit = (token) => {
  const t = (token || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!t) return null;
  // exact match first (avoids "g" swallowing "gel"), then startsWith fallback
  for (const def of UNIT_DEFS) {
    if (def.keys.includes(t)) return def;
  }
  for (const def of UNIT_DEFS) {
    if (def.keys.some((k) => t.startsWith(k))) return def;
  }
  return null;
};

// "800g" | "1.5 kg" | "200 cpr" | "24 × 50 g" | "500 ml" -> normalized metrics
export const parseFormat = (sizeStr) => {
  if (!sizeStr) return null;
  const raw = String(sizeStr).toLowerCase().trim();
  const mult = raw.match(/(\d+[.,]?\d*)\s*[x×*]\s*(\d+[.,]?\d*)/);
  let qty, rest;
  if (mult) {
    qty = parseFloat(mult[1].replace(",", ".")) * parseFloat(mult[2].replace(",", "."));
    rest = raw.slice(mult.index + mult[0].length);
  } else {
    const m = raw.match(/(\d+[.,]?\d*)/);
    if (!m) return null;
    qty = parseFloat(m[1].replace(",", "."));
    rest = raw.slice(m.index + m[0].length);
  }
  if (!qty || Number.isNaN(qty)) return null;
  const unit = detectUnit(rest) || detectUnit(raw.replace(/[\d.,×x*\s]/g, ""));
  if (!unit) return null;
  return { qty, baseQty: qty * unit.factor, family: unit.family, group: unit.group, per100Label: unit.per100 };
};

export const compareAtForSize = (product, size) => {
  const map = product?.compare_at_by_size || {};
  const s = (size || "").trim();
  const v = Number(map[s]);
  return !Number.isNaN(v) && v > 0 ? v : 0;
};

// € per 100 base units for a given size (null when not parseable).
export const pricePer100 = (product, size) => {
  const parsed = parseFormat(size);
  if (!parsed || !parsed.baseQty) return null;
  const price = priceForSize(product, size);
  if (!price) return null;
  return { value: (price / parsed.baseQty) * 100, label: parsed.per100Label, group: parsed.group };
};

// Resolve the recommended badge text: per-product override > global > default.
export const recommendedLabel = (product, settings) =>
  (product?.recommended_label || "").trim() ||
  (settings?.layout?.recommended_badge_label || "").trim() ||
  "Consigliato";

// Auto best €/100 within the dominant tipologia; manual override wins.
export const recommendedSize = (product) => {
  const sizes = product?.sizes || [];
  if (sizes.length < 2) return "";
  const override = (product?.recommended_size || "").trim();
  if (override && sizes.includes(override)) return override;

  const byGroup = {};
  sizes.forEach((sz) => {
    const p = pricePer100(product, sz);
    if (!p) return;
    (byGroup[p.group] = byGroup[p.group] || []).push({ sz, value: p.value });
  });
  const groups = Object.values(byGroup);
  if (groups.length === 0) return "";
  groups.sort((a, b) => b.length - a.length);
  const main = groups[0];
  if (main.length < 2) return "";
  return main.reduce((best, r) => (r.value < best.value ? r : best), main[0]).sz;
};

// Sizes actually purchasable: empty active_sizes = everything defined is on sale
// (back-compat for existing products); otherwise only the chosen ones.
export const activeSizesOf = (product) => {
  const all = product?.sizes || [];
  const active = product?.active_sizes || [];
  return active.length ? all.filter((s) => active.includes(s)) : all;
};

// Grouped comparison rows for the "prezzo per 100" table.
export const formatGroups = (product) => {
  const sizes = activeSizesOf(product);
  const byGroup = {};
  sizes.forEach((sz) => {
    const p = pricePer100(product, sz);
    if (!p) return;
    const price = priceForSize(product, sz);
    (byGroup[p.group] = byGroup[p.group] || { group: p.group, per100Label: p.label, rows: [] })
      .rows.push({ size: sz, price, per100: p.value });
  });
  return Object.values(byGroup)
    .filter((g) => g.rows.length >= 2)
    .map((g) => {
      const best = Math.min(...g.rows.map((r) => r.per100));
      return { ...g, rows: g.rows.map((r) => ({ ...r, isBest: r.per100 === best })) };
    });
};
